import { IsoActionPanel } from '@/components/prescriptions/IsoActionPanel'
import { TestChangeRequestPanel } from '@/components/prescriptions/TestChangeRequestPanel'
import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import type { Doc, Id } from '../../../convex/_generated/dataModel'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import { Odontogram } from '@/components/prescriptions/Odontogram'
import { PhaseRetryForm, type PhaseRetryValues } from '@/components/prescriptions/PhaseRetryForm'
import { ArrowLeft, Edit, Save, X, Upload, Download, Trash2, Send, AlertCircle, CheckCircle, MessageSquare, MessagesSquare, ThumbsUp, ThumbsDown, FileBadge, ClipboardCheck, Link2 } from 'lucide-react'
import { SharedLinkDialog } from '@/components/sharedLinks/SharedLinkDialog'
import { useChatPanel } from '@/components/chat/ChatContext'
import {
    formatCurrency,
    formatImplantTypesNote,
    getStatusColor,
    getStatusLabel,
    parseImplantTypesJson,
} from '@/lib/utils'
import { resolveServicePricingMode } from '../../../convex/lib/applicationType'
import { getClinicalSelectionStatus } from '@/components/prescriptions/clinicalSelectionValidation'
import {
    laboratoryTestLabel,
    parseLaboratoryTests,
} from '../../../convex/lib/laboratoryTests'
import {
    prescriptionDisplayNumber,
    prescriptionNumberValue,
} from '../../../convex/lib/prescriptionNumber'
import {
    getPrimoUpAttachmentValidationError,
    PRIMOUP_ATTACHMENT_ACCEPT,
} from '../../../shared/primoUpIntegration'
import { sortPrescriptionRowsNewestFirst } from '../../../convex/lib/prescriptionRows'

function attachmentSyncPresentation(attachment: any): {
    label: string
    className: string
    description?: string
} {
    const legacyValidationError = attachment.primoup_sync_status === undefined
        ? getPrimoUpAttachmentValidationError({
            filename: attachment.filename,
            contentType: attachment.content_type,
        })
        : null
    if (legacyValidationError) {
        return {
            label: 'Solo in FastLab',
            className: 'border-gray-200 bg-gray-100 text-gray-700',
            description: legacyValidationError,
        }
    }

    switch (attachment.primoup_sync_status) {
        case 'waiting_prescription':
            return { label: 'Attende il numero PrimoUp', className: 'border-amber-200 bg-amber-50 text-amber-900' }
        case 'pending':
            return { label: 'In attesa di invio', className: 'border-amber-200 bg-amber-50 text-amber-900' }
        case 'processing':
            return { label: 'Invio in corso', className: 'border-blue-200 bg-blue-50 text-blue-900' }
        case 'synced':
            return { label: 'Inviato a PrimoUp', className: 'border-green-200 bg-green-50 text-green-900' }
        case 'failed':
            return {
                label: 'Invio non riuscito',
                className: 'border-red-200 bg-red-50 text-red-900',
                description: attachment.primoup_sync_last_error,
            }
        case 'uncertain':
            return {
                label: 'Esito da verificare',
                className: 'border-orange-200 bg-orange-50 text-orange-950',
                description: attachment.primoup_sync_last_error,
            }
        case 'unsupported':
            return {
                label: 'Solo in FastLab',
                className: 'border-gray-200 bg-gray-100 text-gray-700',
                description: attachment.primoup_sync_last_error,
            }
        default:
            return {
                label: 'Non ancora inviato',
                className: 'border-gray-200 bg-gray-100 text-gray-700',
            }
    }
}

type PrescriptionPhaseRow = Doc<'prescription_rows'> & {
    /** Presente nelle risposte legacy che espongono il prezzo a livello riga. */
    prezzo?: number
}


export function PrescriptionDetails() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { profile } = useAuth()
    const { hasPermission } = usePermissions()
    const { openChat } = useChatPanel()


    const [isEditing, setIsEditing] = useState(false)
    const [editStatus, setEditStatus] = useState('')
    const [editPrice, setEditPrice] = useState('')
    const [editPrescriptionNumber, setEditPrescriptionNumber] = useState('')
    const [editPatientName, setEditPatientName] = useState('')
    const [editTint, setEditTint] = useState('')
    const [editColorId, setEditColorId] = useState(0)
    const [editDeliveryDate, setEditDeliveryDate] = useState('')
    const [editDeliveryTime, setEditDeliveryTime] = useState('')
    const [editSelectedTeeth, setEditSelectedTeeth] = useState<string[]>([])
    const [editSelectedImplants, setEditSelectedImplants] = useState<string[]>([])
    const [editImplantTypes, setEditImplantTypes] = useState<Record<string, string>>({})
    const [editSelectionAttempted, setEditSelectionAttempted] = useState(false)
    const [colors, setColors] = useState<any[]>([])
    const [colorsLoading, setColorsLoading] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [uploadingFiles, setUploadingFiles] = useState(false)
    const [attachmentError, setAttachmentError] = useState<string | null>(null)
    const [attachmentNotice, setAttachmentNotice] = useState<string | null>(null)
    const [retryingAttachmentId, setRetryingAttachmentId] = useState<string | null>(null)
    const [shareDialogAttachment, setShareDialogAttachment] = useState<any | null>(null)
    const [isEditingNotes, setIsEditingNotes] = useState(false)
    const [editNotes, setEditNotes] = useState('')
    const [isSyncing, setIsSyncing] = useState(false)
    const [syncError, setSyncError] = useState<string | null>(null)
    const [showFeedbackModal, setShowFeedbackModal] = useState(false)
    const [feedbacks, setFeedbacks] = useState<{[key: string]: {type: 'positive' | 'negative', notes: string}}>({})
    const [showNewProvaModal, setShowNewProvaModal] = useState(false)
    const [showPhaseRetryForm, setShowPhaseRetryForm] = useState(false)
    const canRetryPhase = profile?.active === true && profile.ruolo === 'amministratore'
    const [selectedNewProva, setSelectedNewProva] = useState('')
    const [newProvaDate, setNewProvaDate] = useState('')
    const [newProvaTime, setNewProvaTime] = useState('')
    const [newProvaNotes, setNewProvaNotes] = useState('')
    const [newProvaScanner, setNewProvaScanner] = useState<boolean | undefined>(undefined)
    const [showAttesaProvaModal, setShowAttesaProvaModal] = useState(false)
    const attesaModalDismissedRef = useRef(false)
    const nextPhaseModalOpenedForRef = useRef<string | null>(null)
    const phaseDecisionLinkHandledRef = useRef<string | null>(null)
    const [conformityLoading, setConformityLoading] = useState(false)

    // Notification modals
    const [showNotificationModal, setShowNotificationModal] = useState(false)
    const [notificationConfig, setNotificationConfig] = useState<{
        type: 'success' | 'error' | 'confirm'
        title: string
        message: string
        onConfirm?: () => void
        onCancel?: () => void
        confirmText?: string
        cancelText?: string
    } | null>(null)

    const prescriptionId = id as Id<'prescriptions'>

    // Helper functions for notifications
    const showSuccessModal = (title: string, message: string) => {
        setNotificationConfig({
            type: 'success',
            title,
            message
        })
        setShowNotificationModal(true)
    }

    const showErrorModal = (title: string, message: string) => {
        setNotificationConfig({
            type: 'error',
            title,
            message
        })
        setShowNotificationModal(true)
    }

    const showConfirmModal = (title: string, message: string, onConfirm: () => void, confirmText = 'Conferma', cancelText = 'Annulla') => {
        setNotificationConfig({
            type: 'confirm',
            title,
            message,
            onConfirm,
            confirmText,
            cancelText
        })
        setShowNotificationModal(true)
    }

    const closeNotificationModal = () => {
        setShowNotificationModal(false)
        setNotificationConfig(null)
    }

    // Queries
    const prescription = useQuery(
        api.prescriptions.getPrescriptionDetails,
        prescriptionId ? { prescriptionId } : "skip"
    )

    const displayPhaseName = (row: { prova_id?: number; tipo_prova?: string }) => {
        const tests = parseLaboratoryTests(prescription?.service?.laboratory_tests)
        const match = tests.find((test) =>
            row.prova_id
                ? test.id === row.prova_id
                : test.name.toLocaleLowerCase('it-IT') ===
                    row.tipo_prova?.trim().toLocaleLowerCase('it-IT')
        )
        return match ? laboratoryTestLabel(match) : row.tipo_prova
    }


    // Mutations
    const updatePrescription = useMutation(api.prescriptions.updatePrescriptionAdmin)
    const updateNotes = useMutation(api.prescriptions.updatePrescriptionNotes)
    const generateUploadUrl = useMutation(api.files.generateUploadUrl)
    const saveFileMetadata = useMutation(api.files.saveFileMetadata)
    const deleteAttachment = useMutation(api.files.deleteAttachment)
    const getFileUrlAction = useAction(api.files.getFileUrlAction)
    const syncAttachmentToPrimoUp = useAction(api.primoup.syncPrescriptionAttachmentToPrimoUp)
    const syncToPrimoUp = useAction(api.primoup.syncPrescriptionToPrimoUp)
    const createFeedback = useMutation(api.feedback.createFeedback)
    const createNewProva = useMutation(api.feedback.createNewProva)
    const resolveAttesaProssimaProvaPositive = useMutation(api.feedback.resolveAttesaProssimaProvaPositive)
    const resolveAttesaProssimaProvaNegative = useMutation(api.feedback.resolveAttesaProssimaProvaNegative)
    const retryFeedbackSync = useMutation(api.feedback.retryFeedbackSync)
    const retryPhaseSync = useMutation(api.feedback.retryPhaseSync)
    const availableTests = useQuery(
        api.feedback.getAvailableTests,
        prescriptionId ? { prescription_id: prescriptionId } : "skip"
    )

    useEffect(() => {
        if (!showNewProvaModal || selectedNewProva || !availableTests) return
        const repeatCurrent =
            (prescription?.feedback_sync_kind === 'intermediate' ||
                prescription?.feedback_sync_kind === 'teeth') &&
            prescription?.feedback_sync_value === false
        const defaultPhase = availableTests.find(
            (test: any) =>
                (repeatCurrent ? test.is_current === true : test.is_immediate_next === true) &&
                test.selection_outcome !== 'blocked'
        )
        if (defaultPhase) {
            setSelectedNewProva(String(defaultPhase.id))
        }
    }, [availableTests, prescription, selectedNewProva, showNewProvaModal])

    // PrimoUp actions
    const fetchConformityDocument = useAction(api.primoup.fetchConformityDocument)
    const getColorsAction = useAction(api.primoup.getColors)
    const requestContabilitaExclusion = useMutation(api.prescriptions.requestContabilitaExclusion)
    const decidePhaseApproval = useMutation(api.companyPhases.decidePhaseApproval)
    const recalculatePrice = useMutation(api.adminTools.recalculatePrescriptionPrice)
    const pricingPreview = useQuery(
        api.adminTools.previewPrescriptionPricing,
        hasPermission('manage_prescriptions') && prescriptionId
            ? { prescriptionId }
            : 'skip'
    )
    const [recalculatingPrice, setRecalculatingPrice] = useState(false)
    const [phaseDecisionLoading, setPhaseDecisionLoading] = useState(false)

    const groupMembers = useQuery(
        api.prescriptions.listPrescriptionsByGroup,
        prescription?.prescription_group_id
            ? { group_id: prescription.prescription_group_id }
            : 'skip'
    )

    // Get prescription rows for this prescription
    const prescriptionRows = useQuery(
        api.prescriptions.getPrescriptionRows,
        prescriptionId ? { prescription_id: prescriptionId } : "skip"
    )

    const [exclusionElements, setExclusionElements] = useState<string[]>([])
    const [exclusionNote, setExclusionNote] = useState('')

    const canEdit = hasPermission('manage_prescriptions')
    const canRepairPricing = hasPermission('manage_prescriptions')
    const isSyncedWithPrimoUp = !!(prescription?.external_number_text || prescription?.external_prescription_id)
    const canEditGeneralInfo = canEdit
    const canEditAllGeneralInfo = canEdit && !isSyncedWithPrimoUp
    const canManagePhasePolicies = hasPermission('manage_phase_policies')
    const syncedGroupMembers = groupMembers?.filter(
        (member: any) => member.external_prescription_id || member.external_number_text
    ).length ?? 0
    const pendingGroupMembers = groupMembers?.filter(
        (member: any) => member.phase_skip_status === 'pending'
    ).length ?? 0
    const isPartialGroupSync = syncedGroupMembers > 0 && pendingGroupMembers > 0
    const phaseDecisionParam = searchParams.get('phaseDecision')

    const minAppointmentDateTime = new Date(Date.now() + 60 * 60 * 1000)
    const minDateString = `${minAppointmentDateTime.getFullYear()}-${String(minAppointmentDateTime.getMonth() + 1).padStart(2, '0')}-${String(minAppointmentDateTime.getDate()).padStart(2, '0')}`
    const minTimeString = `${String(minAppointmentDateTime.getHours()).padStart(2, '0')}:${String(minAppointmentDateTime.getMinutes()).padStart(2, '0')}`

    useEffect(() => {
        const loadColors = async () => {
            if (!isEditing || !canEditAllGeneralInfo) return
            setColorsLoading(true)
            try {
                const colorsData = await getColorsAction({})
                setColors(colorsData)
            } catch (error) {
                console.error('Error loading colors:', error)
            } finally {
                setColorsLoading(false)
            }
        }
        loadColors()
    }, [isEditing, canEditAllGeneralInfo, getColorsAction])

    useEffect(() => {
        if (!prescription || prescription === undefined) return
        const linkedCompanyIds = new Set(
            [...(profile?.company_ids ?? []), profile?.company_id].filter(Boolean).map(String)
        )
        const can =
            hasPermission('manage_prescriptions') ||
            prescription.profile_id === profile?._id ||
            prescription.medico_id === profile?._id ||
            ((profile?.ruolo === 'clinic_owner' || profile?.ruolo === 'aso') &&
                linkedCompanyIds.has(String(prescription.company_id)))
        const currentRow = sortPrescriptionRowsNewestFirst(
            prescription.rows ?? []
        )[0]
        const feedbackSyncInProgress =
            prescription.feedback_sync_status === 'pending' ||
            prescription.feedback_sync_status === 'processing'
        const feedbackSyncFailed = prescription.feedback_sync_status === 'failed'
        const feedbackBelongsToCurrentRow =
            Boolean(currentRow) && prescription.feedback_sync_row_id === currentRow._id
        const canConfirmCurrentPhase =
            can && !prescription.iso_action_pending &&
            prescription.stato === 'spedita' &&
            prescription.attesa_prossima_prova === true &&
            !feedbackSyncInProgress &&
            !feedbackSyncFailed &&
            (!prescription.feedback_sync_status || !feedbackBelongsToCurrentRow)

        if (!canConfirmCurrentPhase) {
            // L'effetto in precedenza sapeva soltanto aprire il modale: durante
            // la coda PrimoUp poteva quindi riaprirlo e lasciarlo bloccato sopra
            // alla schermata anche dopo il cambio di stato.
            setShowAttesaProvaModal(false)
            attesaModalDismissedRef.current = false
        } else if (!attesaModalDismissedRef.current) {
            setShowAttesaProvaModal(true)
        }
    }, [prescription, profile])

    useEffect(() => {
        if (phaseDecisionParam !== 'approve' && phaseDecisionParam !== 'reject') return
        if (!prescription) return

        const clearDecisionParam = () => {
            const next = new URLSearchParams(searchParams)
            next.delete('phaseDecision')
            setSearchParams(next, { replace: true })
        }
        const linkKey = `${prescription._id}:${phaseDecisionParam}`
        if (phaseDecisionLinkHandledRef.current === linkKey) return
        phaseDecisionLinkHandledRef.current = linkKey

        if (!canManagePhasePolicies || prescription.phase_skip_status !== 'pending') {
            clearDecisionParam()
            showErrorModal(
                'Richiesta non disponibile',
                canManagePhasePolicies
                    ? 'Questa richiesta non è più in attesa.'
                    : 'Non hai il permesso di approvare o rifiutare questa richiesta.'
            )
            return
        }

        const approve = phaseDecisionParam === 'approve'
        setNotificationConfig({
            type: 'confirm',
            title: approve ? 'Conferma approvazione salto' : 'Conferma rifiuto salto',
            message: approve
                ? 'Vuoi approvare il salto di fase e avviare la sincronizzazione con PrimoUp?'
                : 'Vuoi rifiutare il salto di fase? PrimoUp non verrà chiamato.',
            confirmText: approve ? 'Approva e sincronizza' : 'Rifiuta',
            cancelText: 'Annulla',
            onCancel: () => {
                clearDecisionParam()
                closeNotificationModal()
            },
            onConfirm: () => {
                clearDecisionParam()
                closeNotificationModal()
                setPhaseDecisionLoading(true)
                void decidePhaseApproval({
                    prescription_id: prescriptionId,
                    decision: phaseDecisionParam,
                })
                    .then(() => {
                        showSuccessModal(
                            approve ? 'Salto approvato' : 'Salto rifiutato',
                            approve
                                ? isPartialGroupSync
                                    ? 'Retry dei soli elementi mancanti messo in coda.'
                                    : 'Sincronizzazione messa in coda.'
                                : 'PrimoUp non è stato chiamato.'
                        )
                    })
                    .catch((error) => {
                        showErrorModal(
                            'Decisione non salvata',
                            error instanceof Error ? error.message : 'Errore sconosciuto'
                        )
                    })
                    .finally(() => setPhaseDecisionLoading(false))
            },
        })
        setShowNotificationModal(true)
    }, [
        canManagePhasePolicies,
        decidePhaseApproval,
        isPartialGroupSync,
        phaseDecisionParam,
        prescription,
        prescriptionId,
        searchParams,
        setSearchParams,
    ])

    useEffect(() => {
        if (prescription?.iso_action_pending) {
            setShowNewProvaModal(false)
            setShowFeedbackModal(false)
            setShowAttesaProvaModal(false)
        }
    }, [prescription?.iso_action_pending])

    // Check if user can edit notes and upload files
    const linkedCompanyIds = new Set(
        [...(profile?.company_ids ?? []), profile?.company_id].filter(Boolean).map(String)
    )
    const canEditNotes = prescription && profile && (
        hasPermission('manage_prescriptions') ||
        prescription.profile_id === profile._id ||
        prescription.medico_id === profile._id ||
        ((profile.ruolo === 'clinic_owner' || profile.ruolo === 'aso') && linkedCompanyIds.has(String(prescription.company_id)))
    )

    useEffect(() => {
        if (
            !prescription ||
            prescription.iso_action_pending ||
            !canEditNotes ||
            showNotificationModal ||
            prescription.feedback_sync_status !== 'synced' ||
            prescription.richiede_nuova_prova_post_feedback !== true
        ) {
            return
        }

        const promptKey =
            prescription.feedback_sync_operation_id ??
            `${prescription._id}:${prescription.feedback_sync_completed_at ?? prescription.updated_at}`
        if (nextPhaseModalOpenedForRef.current === promptKey) return

        nextPhaseModalOpenedForRef.current = promptKey
        setShowAttesaProvaModal(false)
        setSelectedNewProva('')
        setShowNewProvaModal(true)
    }, [
        canEditNotes,
        prescription,
        showNotificationModal,
    ])

    if (!prescriptionId) {
        return <div>ID prescrizione non valido</div>
    }

    if (prescription === undefined) {
        return <Loading />
    }

    if (!prescription) {
        return <div>Prescrizione non trovata</div>
    }

    const primoUpSyncStatus =
        prescription.lab_api_last_response?.sync_status as string | undefined
    const orderedPrescriptionRows = sortPrescriptionRowsNewestFirst<PrescriptionPhaseRow>(
        (prescription.rows ?? []) as PrescriptionPhaseRow[]
    )
    const currentPhaseRow = orderedPrescriptionRows[0]
    const pastPhaseRows = orderedPrescriptionRows.slice(1)

    const handleRecalculatePrice = async () => {
        if (!pricingPreview?.mismatch) return
        const ok = window.confirm(
            `Allineare il prezzo all'odontogramma?\n\n` +
                `Tipo: ${pricingPreview.applicationKind} (${pricingPreview.billableCount} elementi)\n` +
                `Attuale: ${formatCurrency(pricingPreview.currentPrice)}\n` +
                `Corretto: ${formatCurrency(pricingPreview.expectedPrice)}\n\n` +
                'Aggiorna PrezzoNetto in contabilità e API pagamento.'
        )
        if (!ok) return
        setRecalculatingPrice(true)
        try {
            const res = await recalculatePrice({ prescriptionId })
            showSuccessModal(
                'Prezzo aggiornato',
                `${formatCurrency(res.previousPrice)} → ${formatCurrency(res.newPrice)} (${res.billableCount} elementi).`
            )
            if (isEditing) {
                setEditPrice(res.newPrice.toFixed(2))
            }
        } catch (error) {
            showErrorModal(
                'Errore ricalcolo',
                error instanceof Error ? error.message : 'Errore sconosciuto'
            )
        } finally {
            setRecalculatingPrice(false)
        }
    }

    const handleEdit = () => {
        setIsEditing(true)
        setEditSelectionAttempted(false)
        setEditStatus(prescription.stato || '')
        setEditPrice(prescription.PrezzoNetto?.toString() || '')
        setEditPrescriptionNumber(prescription.numero_prescrizione || '')
        setEditPatientName(prescription.NomePaziente || '')
        setEditTint(currentPhaseRow?.tinta || '')
        setEditColorId(currentPhaseRow?.color_id || 0)
        setEditDeliveryDate(currentPhaseRow?.dataapp ? new Date(currentPhaseRow.dataapp).toISOString().slice(0, 10) : '')
        setEditDeliveryTime(currentPhaseRow?.timeapp || '')
        try {
            setEditSelectedTeeth(prescription.denti_selezionati ? JSON.parse(prescription.denti_selezionati) : [])
        } catch (error) {
            setEditSelectedTeeth([])
        }
        try {
            setEditSelectedImplants(prescription.impianti_selezionati ? JSON.parse(prescription.impianti_selezionati) : [])
        } catch (error) {
            setEditSelectedImplants([])
        }
        setEditImplantTypes(parseImplantTypesJson((prescription as any).impianti_catalog_ids))
    }

    const handleSave = async () => {
        setEditSelectionAttempted(true)
        try {
            let deliveryTimestamp: number | undefined = undefined
            const originalTint = currentPhaseRow?.tinta || ''
            const originalColorId = currentPhaseRow?.color_id || 0
            const tintChanged = canEditAllGeneralInfo && (editTint !== originalTint || editColorId !== originalColorId)

            if (canEditAllGeneralInfo) {
                if (tintChanged && (!editTint || !editColorId)) {
                    showErrorModal('Errore Validazione', 'Seleziona una tinta valida')
                    return
                }

                if (!editDeliveryDate || !editDeliveryTime) {
                    showErrorModal('Errore Validazione', 'Data e ora consegna sono obbligatorie')
                    return
                }

                const selectionStatus = getClinicalSelectionStatus({
                    selectedTeeth: editSelectedTeeth,
                    selectedImplants: editSelectedImplants,
                    implantTypes: editImplantTypes,
                    pricingMode: resolveServicePricingMode(prescription.service),
                })
                if (!selectionStatus.valid) {
                    showErrorModal(
                        selectionStatus.title,
                        selectionStatus.description
                    )
                    return
                }

                const combinedDateTime = new Date(`${editDeliveryDate}T${editDeliveryTime}:00`)
                const minAllowedDateTime = new Date(Date.now() + 60 * 60 * 1000)
                if (combinedDateTime.getTime() < minAllowedDateTime.getTime()) {
                    showErrorModal(
                        'Errore Validazione',
                        `L'appuntamento deve essere almeno un'ora dopo adesso (${minAllowedDateTime.toLocaleDateString('it-IT')} ${minAllowedDateTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })})`
                    )
                    return
                }
                deliveryTimestamp = combinedDateTime.getTime()
            }

            await updatePrescription({
                prescriptionId,
                status: editStatus && editStatus !== prescription.stato ? editStatus : undefined,
                prezzo_totale: editPrice ? parseFloat(editPrice) : undefined,
                numero_prescrizione: editPrescriptionNumber || undefined,
                nome_paziente: canEditAllGeneralInfo ? editPatientName : undefined,
                tinta: canEditAllGeneralInfo && tintChanged ? editTint : undefined,
                color_id: canEditAllGeneralInfo && tintChanged ? editColorId : undefined,
                dataapp: canEditAllGeneralInfo ? deliveryTimestamp : undefined,
                timeapp: canEditAllGeneralInfo ? editDeliveryTime : undefined,
                denti_selezionati: canEditAllGeneralInfo ? editSelectedTeeth : undefined,
                impianti_selezionati: canEditAllGeneralInfo ? editSelectedImplants : undefined,
                impianti_catalog_ids: canEditAllGeneralInfo ? editImplantTypes : undefined,
            })
            console.log(JSON.stringify(editStatus, null, 2))
            setIsEditing(false)
            setEditSelectionAttempted(false)
        } catch (error) {
            console.error('Error updating prescription:', error)
            showErrorModal(
                'Modifiche non salvate',
                'Non è stato possibile salvare le modifiche. Controlla i dati e riprova; se il problema continua, contatta PrimoLab.'
            )
        }
    }


    const handleCancel = () => {
        setIsEditing(false)
        setEditSelectionAttempted(false)
        setEditStatus('')
        setEditPrice('')
    }

    const handleEditNotes = () => {
        setIsEditingNotes(true)
        setEditNotes(currentPhaseRow?.note || '')
    }

    const handleSaveNotes = async () => {
        try {
            await updateNotes({
                prescriptionId,
                note: editNotes,
            })
            setIsEditingNotes(false)
        } catch (error) {
            console.error('Error updating notes:', error)
        }
    }

    const handleCancelNotes = () => {
        setIsEditingNotes(false)
        setEditNotes('')
    }

    // Function to determine application type like in PrescriptionForm
    const getApplicationTypeForFeedback = (selectedTeeth: string[]) => {
        // Per una prestazione per dente, il pulsante "arcata" è solo una
        // scorciatoia di selezione: il feedback resta su ciascuno dei 16 denti.
        if (resolveServicePricingMode(prescription?.service) === 'per_tooth') {
            return { type: 'denti', zones: [...new Set(selectedTeeth.map(String))].sort() }
        }
        const upperTeeth = selectedTeeth.filter(tooth => {
            const num = parseInt(tooth);
            return num >= 11 && num <= 28;
        });
        
        const lowerTeeth = selectedTeeth.filter(tooth => {
            const num = parseInt(tooth);
            return num >= 31 && num <= 48;
        });

        // Entrambe le arcate → 2 elementi (non "bocca")
        if (upperTeeth.length >= 16 && lowerTeeth.length >= 16) {
            return { type: "arcate", zones: ["arcata_superiore", "arcata_inferiore"] };
        }
        
        if (upperTeeth.length >= 16 && lowerTeeth.length === 0) {
            return { type: "arcata", zones: ["arcata_superiore"] };
        }
        
        if (lowerTeeth.length >= 16 && upperTeeth.length === 0) {
            return { type: "arcata", zones: ["arcata_inferiore"] };
        }
        
        return { type: "denti", zones: selectedTeeth };
    };

    // Function to get display label for feedback zones
    const getFeedbackZoneLabel = (zone: string) => {
        const zoneLabels: { [key: string]: string } = {
            'arcata_superiore': 'Arcata Superiore',
            'arcata_inferiore': 'Arcata Inferiore'
        };
        
        // If it's a tooth number, return "Dente X"
        if (!zoneLabels[zone]) {
            return `Dente ${zone}`;
        }
        
        return zoneLabels[zone];
    };

    // Selezione anatomica e unità economica sono due concetti distinti:
    // una corona su "arcata superiore" resta una corona per ciascuno dei 16 denti.
    const getSelectedElementsInfo = (overrideTeeth?: string[], overrideImplants?: string[]) => {
        if (!overrideTeeth && !overrideImplants && !prescription?.denti_selezionati && !prescription?.impianti_selezionati) {
            return { description: 'Nessuna selezione', selectedElements: ['Nessuna selezione'] };
        }

        let selectedTeeth: string[] = overrideTeeth || [];
        let selectedImplants: string[] = overrideImplants || [];

        if (!overrideTeeth) {
            try {
                selectedTeeth = prescription.denti_selezionati ? JSON.parse(prescription.denti_selezionati) : [];
            } catch (error) {
                console.error('Error parsing selected teeth:', error);
            }
        }

        if (!overrideImplants) {
            try {
                selectedImplants = prescription.impianti_selezionati ? JSON.parse(prescription.impianti_selezionati) : [];
            } catch (error) {
                console.error('Error parsing selected implants:', error);
            }
        }

        const selectedForPricing = selectedTeeth;

        // Use same logic as feedback for determining type
        const applicationType = getApplicationTypeForFeedback(selectedTeeth);

        let description = '';
        let selectedElements: string[] = [];
        if (applicationType.type === 'arcate' || applicationType.type === 'bocca') {
            selectedElements = [
                `Arcata superiore (${selectedTeeth.filter((tooth) => {
                    const n = parseInt(tooth, 10)
                    return n >= 11 && n <= 28
                }).length} denti)`,
                `Arcata inferiore (${selectedTeeth.filter((tooth) => {
                    const n = parseInt(tooth, 10)
                    return n >= 31 && n <= 48
                }).length} denti)`,
                `Denti: ${selectedTeeth.join(', ')}`,
            ];
        } else if (applicationType.type === 'arcata') {
            const isUpper = applicationType.zones.includes('arcata_superiore');
            const archLabel = isUpper ? 'Arcata superiore' : 'Arcata inferiore';
            selectedElements = [
                `${archLabel} (${selectedTeeth.length} denti)`,
                `Denti: ${selectedTeeth.join(', ')}`,
            ];
        } else {
            const details: string[] = []
            if (selectedTeeth.length > 0) {
                details.push(`Denti: ${selectedTeeth.join(',')}`)
            }
            if (selectedImplants.length > 0) {
                const types = parseImplantTypesJson((prescription as any)?.impianti_tipi)
                const implantLine = formatImplantTypesNote(selectedImplants, types)
                details.push(implantLine || `Impianti: ${selectedImplants.join(',')}`)
            }
            selectedElements = details.length > 0 ? details : ['Nessuna selezione'];
        }

        if (selectedImplants.length > 0 && applicationType.type !== 'denti') {
            const types = parseImplantTypesJson((prescription as any)?.impianti_tipi)
            const implantLine = formatImplantTypesNote(selectedImplants, types)
            selectedElements.push(implantLine || `Impianti: ${selectedImplants.join(', ')}`)
        }

        const storedPricingMode = (prescription as any)?.pricing_summary?.mode
        const pricingMode = storedPricingMode === 'per_tooth' || storedPricingMode === 'per_mouth'
            ? storedPricingMode
            : 'per_arch'
        const billableCount = pricingMode === 'per_tooth'
            ? Array.from(new Set(selectedForPricing)).length
            : pricingMode === 'per_mouth'
                ? (selectedForPricing.length > 0 ? 1 : 0)
            : applicationType.type === 'arcate' || applicationType.type === 'bocca'
                ? 2
                : applicationType.type === 'arcata'
                    ? 1
                    : Array.from(new Set(selectedForPricing)).length
        description = pricingMode === 'per_tooth'
            ? `Per dente · ${billableCount} ${billableCount === 1 ? 'elemento' : 'elementi'}`
            : pricingMode === 'per_mouth'
                ? 'Per bocca · 1 prestazione'
            : applicationType.type === 'arcata' || applicationType.type === 'arcate' || applicationType.type === 'bocca'
                ? `Per arcata · ${billableCount} ${billableCount === 1 ? 'arcata' : 'arcate'}`
                : `Per elemento · ${billableCount} ${billableCount === 1 ? 'elemento' : 'elementi'}`

        return {
            description: description,
            selectedElements: selectedElements,
            billableCount,
            pricingMode,
        };
    };

    const persistedSelectionInfo = getSelectedElementsInfo();

    // Feedback functions
    const handleOpenFeedback = () => {
        if (!prescription?.denti_selezionati) return

        try {
            const selectedTeeth = JSON.parse(prescription.denti_selezionati)
            const applicationType = getApplicationTypeForFeedback(selectedTeeth)
            const initialFeedbacks: {[key: string]: {type: 'positive' | 'negative', notes: string}} = {}

            // Create feedback entries based on application type
            applicationType.zones.forEach((zone: string) => {
                initialFeedbacks[zone] = { type: 'positive', notes: '' }
            })

            setFeedbacks(initialFeedbacks)
            setShowFeedbackModal(true)
        } catch (error) {
            console.error('Error parsing selected teeth:', error)
        }
    }

    const handleFeedbackChange = (tooth: string, type: 'positive' | 'negative', notes: string) => {
        setFeedbacks(prev => ({
            ...prev,
            [tooth]: { type, notes }
        }))
    }

    const handleSubmitFeedback = async () => {
        if (!prescriptionRows || prescriptionRows.length === 0) return

        try {
            const latestRow = sortPrescriptionRowsNewestFirst(prescriptionRows)[0]
            if (!latestRow) return
            const feedbackArray = Object.entries(feedbacks).map(([tooth, feedback]) => ({
                tooth_number: tooth,
                feedback_type: feedback.type,
                notes: feedback.notes || undefined,
            }))

            const result = await createFeedback({
                prescription_id: prescriptionId,
                prescription_row_id: latestRow._id,
                feedbacks: feedbackArray,
            })
            setShowFeedbackModal(false)
            showSuccessModal(
                result.syncStatus === 'synced' ? 'Feedback salvato' : 'Feedback preso in carico',
                result.syncStatus === 'synced'
                    ? 'Il feedback è stato registrato.'
                    : 'La sincronizzazione con PrimoUp prosegue sul server. Puoi lasciare questa pagina.'
            )
        } catch (error) {
            console.error('Error submitting feedback:', error)
            const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto durante il salvataggio del feedback'
            showErrorModal(
                'Errore Feedback',
                `Non è stato possibile salvare il feedback.\n\nDettagli: ${errorMessage}\n\nI dati inseriti sono stati mantenuti. Riprova o contatta l'assistenza.`
            )
            // Keep modal open so user doesn't lose data
        }
    }

    const handleCreateNewProva = async () => {
        if (!prescriptionRows || prescriptionRows.length === 0 || !selectedNewProva) return

        // Validate date if provided
        if (newProvaDate && !newProvaTime) {
            showErrorModal('Errore Validazione', 'Se inserisci una data, devi inserire anche l\'orario')
            return
        }

        try {
            const selectedTest = availableTests?.find((test: any) => test.id.toString() === selectedNewProva)
            if (!selectedTest) return
            const latestRow = sortPrescriptionRowsNewestFirst(prescriptionRows)[0]
            if (!latestRow) return

            // Convert date to timestamp if provided
            let dataappTimestamp: number | undefined = undefined
            if (newProvaDate) {
                dataappTimestamp = new Date(newProvaDate).getTime()
            }

            // Prima salva localmente: la mutation decide se serve approvazione.
            const createResult = await createNewProva({
                prescription_id: prescriptionId,
                original_prescription_row_id: latestRow._id,
                new_tipo_prova: selectedTest.name,
                new_prova_id: selectedTest.id,
                notes: newProvaNotes || undefined,
                dataapp: dataappTimestamp,
                timeapp: newProvaTime || undefined,
                scansione_intraorale: newProvaScanner,
            })

            // Success - close modal and reset form
            setShowNewProvaModal(false)
            setSelectedNewProva('')
            setNewProvaDate('')
            setNewProvaTime('')
            setNewProvaNotes('')
            setNewProvaScanner(undefined)
            showSuccessModal(
                createResult.requiresApproval ? 'Approvazione richiesta' : 'Successo',
                createResult.requiresApproval
                    ? 'La nuova fase è salvata in attesa del laboratorio. PrimoUp non è stato chiamato.'
                    : createResult.phaseSyncStatus === 'pending'
                        ? 'Nuova prova salvata. La sincronizzazione con PrimoUp prosegue sul server.'
                        : 'Nuova prova creata con successo!'
            )
        } catch (error) {
            console.error('Error creating new prova:', error)
            // User-friendly error message without technical details
            showErrorModal(
                'Errore Creazione Prova',
                error instanceof Error
                    ? error.message
                    : 'Non è stato possibile creare la nuova prova.'
            )
            // Keep modal open so user doesn't lose data
        }
    }

    const handleAttesaProvaConforme = async () => {
        // Impedisce all'effetto reattivo di riaprire il vecchio modale mentre
        // il feedback è in coda verso PrimoUp.
        attesaModalDismissedRef.current = true
        setShowAttesaProvaModal(false)
        try {
            await resolveAttesaProssimaProvaPositive({ prescription_id: prescriptionId })
        } catch (error) {
            attesaModalDismissedRef.current = false
            console.error(error)
            const msg = error instanceof Error ? error.message : 'Operazione non riuscita'
            showErrorModal('Errore', msg)
        }
    }

    const handleAttesaProvaNonConforme = async () => {
        attesaModalDismissedRef.current = true
        setShowAttesaProvaModal(false)
        try {
            await resolveAttesaProssimaProvaNegative({ prescription_id: prescriptionId })
        } catch (error) {
            attesaModalDismissedRef.current = false
            console.error(error)
            const msg = error instanceof Error ? error.message : 'Operazione non riuscita'
            showErrorModal('Errore', msg)
        }
    }

    const handleRetryFeedbackSync = async () => {
        try {
            await retryFeedbackSync({ prescription_id: prescriptionId })
            showSuccessModal('Retry avviato', 'Il feedback verrà risincronizzato dal server.')
        } catch (error) {
            showErrorModal(
                'Retry non avviato',
                error instanceof Error ? error.message : 'Operazione non riuscita'
            )
        }
    }

    const handleRetryPhaseSync = async (values: PhaseRetryValues) => {
        if (!canRetryPhase || !currentPhaseRow) throw new Error('Operazione riservata agli amministratori.')
        await retryPhaseSync({
            prescription_id: prescriptionId,
            expected_row_id: currentPhaseRow._id,
            ...values,
        })
        setShowPhaseRetryForm(false)
        showSuccessModal('Invio avviato', 'I dati sono aggiornati. La fase è in sincronizzazione con PrimoUp.')
    }

    const downloadBase64AsFile = (base64: string, mime: string, filename: string) => {
        const binary = atob(base64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        const blob = new Blob([bytes], { type: mime })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const getConformityDownloadUrl = (data: unknown): string | null => {
        if (!data || typeof data !== 'object' || Array.isArray(data)) return null

        const payload = data as Record<string, unknown>
        const nested = payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
            ? payload.data as Record<string, unknown>
            : null
        const candidates = [
            payload.download_url,
            payload.url,
            nested?.download_url,
            nested?.url,
        ]

        return candidates.find((value): value is string =>
            typeof value === 'string' && /^https:\/\//i.test(value)
        ) || null
    }

    const openConformityDownload = (url: string) => {
        const a = document.createElement('a')
        a.href = url
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
    }

    const handleConformityDocument = async () => {
        setConformityLoading(true)
        try {
            const result = await fetchConformityDocument({ prescriptionId })
            if (result.kind === 'error') {
                showErrorModal('Dichiarazione di conformità', result.message)
            } else if (result.kind === 'json') {
                const downloadUrl = getConformityDownloadUrl(result.data)
                if (downloadUrl) {
                    openConformityDownload(downloadUrl)
                    showSuccessModal(
                        'Dichiarazione di conformità',
                        'Il documento è stato aperto in una nuova scheda.'
                    )
                } else {
                    showErrorModal(
                        'Dichiarazione di conformità',
                        'PrimoLab ha risposto senza fornire il collegamento al documento. Riprova più tardi.'
                    )
                }
            } else {
                const ext = result.contentType.includes('pdf')
                    ? 'pdf'
                    : (result.contentType.split('/')[1] || 'bin').split(';')[0]
                const safeName = String(
                    prescriptionNumberValue(prescription)
                ).replace(/\s+/g, '-')
                downloadBase64AsFile(
                    result.base64,
                    result.contentType,
                    `dichiarazione-conformita-${safeName}.${ext}`
                )
                showSuccessModal('Download', 'Il file è stato scaricato.')
            }
        } catch (error) {
            console.error(error)
            const raw = error instanceof Error ? error.message : 'Richiesta non riuscita'
            // Convex spesso avvolge: "[CONVEX A(...)] ... Server Error\n... actual message"
            const msg = raw
                .replace(/^\[CONVEX[^\]]*\]\s*/i, '')
                .replace(/\[Request ID:[^\]]*\]\s*/i, '')
                .replace(/^Server Error\s*/i, '')
                .replace(/\s*Called by client\s*$/i, '')
                .trim() || raw
            showErrorModal('Dichiarazione di conformità', msg)
        } finally {
            setConformityLoading(false)
        }
    }

    const handleSyncToPrimoUp = async () => {
        if (!prescriptionId) return

        setIsSyncing(true)
        setSyncError(null)

        try {
            const result = await syncToPrimoUp({ prescriptionId })
            console.log('Sync successful:', result)
            showSuccessModal('Sincronizzazione Completata', 'La prescrizione è stata sincronizzata con successo su PrimoUp.')
        } catch (error) {
            console.error('Sync error:', error)
            let errorMessage = error instanceof Error ? error.message : 'Errore durante la sincronizzazione'

            // Estrai messaggio utile dall'errore PrimoUp
            if (errorMessage.includes('PrimoUp sync error:')) {
                const match = errorMessage.match(/PrimoUp sync error: (\d+) ([^-]+) - (.+)/s)
                if (match) {
                    const status = match[1]
                    const statusText = match[2].trim()
                    let errorBody = match[3].trim()
                    
                    // Se l'errore contiene ancora HTML, puliscilo
                    if (errorBody.includes('<!DOCTYPE') || errorBody.includes('<HTML')) {
                        const h1Match = errorBody.match(/<H1>([^<]+)<\/H1>/i)
                        const titleMatch = errorBody.match(/<TITLE>([^<]+)<\/TITLE>/i)
                        if (h1Match) {
                            errorBody = h1Match[1]
                        } else if (titleMatch) {
                            errorBody = titleMatch[1]
                        } else {
                            errorBody = statusText
                        }
                    }

                    // Ricostruisci un messaggio pulito
                    if (status === '504') {
                        errorMessage = `504 Gateway Timeout: Il server ha impiegato troppo tempo a rispondere. Questo non è un errore del tuo sistema. Per favore, attendi un minuto e riprova.`
                    } else if (status === '422') {
                        // Prova a parsare il JSON dalla risposta 422
                        try {
                            const jsonMatch = errorBody.match(/\{.+?\}/s)
                            if (jsonMatch) {
                                const errorData = JSON.parse(jsonMatch[0])
                                let userFriendlyMessage = '422 Dati Non Validi: '
                                if (errorData.message) {
                                    userFriendlyMessage += errorData.message
                                }
                                if (errorData.errors) {
                                    const errorDetails = Object.entries(errorData.errors)
                                        .map(([field, messages]) => {
                                            const fieldName = field === 'date' ? 'Data' : field
                                            const errorMessages = Array.isArray(messages) ? messages.join(', ') : messages
                                            return `${fieldName}: ${errorMessages}`
                                        })
                                        .join('; ')
                                    if (errorDetails) {
                                        userFriendlyMessage += '\n\nDettagli: ' + errorDetails
                                    }
                                }
                                errorMessage = userFriendlyMessage
                            } else {
                                errorMessage = `422 Unprocessable Entity: ${errorBody}`
                            }
                        } catch {
                            errorMessage = `422 Unprocessable Entity: ${errorBody}`
                        }
                    } else {
                        errorMessage = `${status} ${statusText}: ${errorBody}`
                    }
                }
            }

            // Check if it's a 504 Gateway Timeout error
            const is504Error = errorMessage.includes('504') || errorMessage.toLowerCase().includes('gateway timeout')

            // Check if it's a 422 Unprocessable Entity error
            const is422Error = errorMessage.includes('422')

            if (is504Error) {
                setSyncError('504 Gateway Timeout: PrimoUp non ha risposto in tempo. Attendi un minuto e verifica lo stato prima di riprovare, per evitare una prescrizione duplicata.')
            } else if (is422Error) {
                // Try to parse the 422 error response
                try {
                    const jsonMatch = errorMessage.match(/\{.*\}/)
                    if (jsonMatch) {
                        const errorData = JSON.parse(jsonMatch[0])
                        let userFriendlyMessage = '422 Dati Non Validi: '

                        if (errorData.message) {
                            userFriendlyMessage += errorData.message
                        }

                        if (errorData.errors) {
                            const errorDetails = Object.entries(errorData.errors)
                                .map(([field, messages]) => {
                                    const fieldName = field === 'date' ? 'Data' : field
                                    const errorMessages = Array.isArray(messages) ? messages.join(', ') : messages
                                    return `${fieldName}: ${errorMessages}`
                                })
                                .join('; ')

                            if (errorDetails) {
                                userFriendlyMessage += '\n\nDettagli: ' + errorDetails
                            }
                        }

                        setSyncError(userFriendlyMessage)
                    } else {
                        setSyncError(errorMessage)
                    }
                } catch (parseError) {
                    setSyncError(errorMessage)
                }
            } else {
                setSyncError(errorMessage)
            }
        } finally {
            setIsSyncing(false)
        }
    }

    const handlePhaseDecision = async (decision: 'approve' | 'reject') => {
        setPhaseDecisionLoading(true)
        try {
            await decidePhaseApproval({ prescription_id: prescriptionId, decision })
            showSuccessModal(
                decision === 'approve' ? 'Salto approvato' : 'Salto rifiutato',
                decision === 'approve'
                    ? isPartialGroupSync
                        ? 'È stato messo in coda il retry dei soli elementi del gruppo non ancora sincronizzati.'
                        : 'La sincronizzazione corretta è stata messa in coda.'
                    : 'PrimoUp non è stato chiamato.'
            )
        } catch (error) {
            showErrorModal(
                'Decisione non salvata',
                error instanceof Error ? error.message : 'Errore sconosciuto'
            )
        } finally {
            setPhaseDecisionLoading(false)
        }
    }
    const handleFileUpload = async () => {
        if (selectedFiles.length === 0) return

        setUploadingFiles(true)
        setAttachmentError(null)
        setAttachmentNotice(null)
        const failures: string[] = []
        let syncedCount = 0
        let waitingCount = 0
        try {
            for (const file of selectedFiles) {
                const validationError = getPrimoUpAttachmentValidationError({
                    filename: file.name,
                    contentType: file.type,
                })
                if (validationError) {
                    failures.push(`${file.name}: ${validationError}`)
                    continue
                }
                try {
                    const uploadUrl = await generateUploadUrl({})
                    const uploadResult = await fetch(uploadUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': file.type },
                        body: file,
                    })
                    if (!uploadResult.ok) throw new Error('caricamento locale non riuscito')
                    const { storageId } = await uploadResult.json()
                    const attachmentId = await saveFileMetadata({
                        prescriptions_id: prescriptionId,
                        file_id: storageId,
                        filename: file.name,
                        content_type: file.type,
                        file_size: file.size,
                    })
                    const syncResult = await syncAttachmentToPrimoUp({ attachmentId })
                    if (syncResult.status === 'synced') {
                        syncedCount += 1
                    } else if (
                        syncResult.status === 'waiting_prescription' ||
                        syncResult.status === 'processing'
                    ) {
                        waitingCount += 1
                    } else {
                        failures.push(
                            `${file.name}: ${syncResult.message ?? 'invio a PrimoUp non riuscito'}`
                        )
                    }
                } catch (error) {
                    failures.push(
                        `${file.name}: ${error instanceof Error ? error.message : 'caricamento non riuscito'}`
                    )
                }
            }

            setSelectedFiles([])
            if (failures.length > 0) {
                setAttachmentError(failures.join(' · '))
            }
            if (syncedCount > 0 || waitingCount > 0) {
                setAttachmentNotice(
                    [
                        syncedCount > 0 ? `${syncedCount} inviati a PrimoUp` : '',
                        waitingCount > 0 ? `${waitingCount} in attesa della prescrizione PrimoUp` : '',
                    ].filter(Boolean).join(' · ')
                )
            }
        } finally {
            setUploadingFiles(false)
        }
    }

    const handleDeleteAttachment = async (attachmentId: Id<'prescription_attachments'>) => {
        setAttachmentError(null)
        try {
            await deleteAttachment({ attachment_id: attachmentId })
        } catch (error) {
            console.error('Error deleting attachment:', error)
            setAttachmentError(
                error instanceof Error ? error.message : 'Eliminazione dell’allegato non riuscita.'
            )
        }
    }

    const handleRetryAttachmentSync = async (attachmentId: Id<'prescription_attachments'>) => {
        setRetryingAttachmentId(String(attachmentId))
        setAttachmentError(null)
        setAttachmentNotice(null)
        try {
            const result = await syncAttachmentToPrimoUp({ attachmentId })
            if (result.status === 'synced') {
                setAttachmentNotice('Allegato inviato correttamente a PrimoUp.')
            } else {
                setAttachmentError(
                    result.message ?? 'L’allegato non è stato inviato a PrimoUp.'
                )
            }
        } catch (error) {
            setAttachmentError(
                error instanceof Error ? error.message : 'Invio a PrimoUp non riuscito.'
            )
        } finally {
            setRetryingAttachmentId(null)
        }
    }

    const handleDownloadAttachment = async (fileId: Id<'_storage'>, filename: string) => {
        try {
            // Get the file URL from Convex
            const url = await getFileUrlAction({ file_id: fileId })

            // Create a temporary link and trigger download
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            link.target = '_blank'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error('Error downloading file:', error)
            alert('Errore durante il download del file')
        }
    }

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('it-IT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }
    const requiresNewPhase =
        (prescription as any).richiede_nuova_prova_post_esclusione === true ||
        (prescription as any).richiede_nuova_prova_post_rifiuto_fase === true ||
        (prescription as any).richiede_nuova_prova_post_feedback === true
    const isWaitingForPhaseConfirmation =
        prescription.attesa_prossima_prova === true && prescription.stato === 'spedita'
    const canConfirmCurrentPhase =
        canEditNotes && !prescription.iso_action_pending && !prescription.test_change_pending &&
        isWaitingForPhaseConfirmation &&
        prescription.feedback_sync_status !== 'pending' &&
        prescription.feedback_sync_status !== 'processing' &&
        prescription.feedback_sync_status !== 'failed' &&
        (!prescription.feedback_sync_status ||
            prescription.feedback_sync_row_id !== currentPhaseRow?._id)
    const hasRecordedFeedbackForWaitingPhase =
        isWaitingForPhaseConfirmation &&
        Boolean(prescription.feedback_sync_status) &&
        prescription.feedback_sync_row_id === currentPhaseRow?._id
    const displayedCaseStatus = prescription.test_change_pending ? 'Cambio prova in gestione' : prescription.iso_action_pending ? 'Intervento ISO richiesto' : requiresNewPhase
        ? 'Nuova fase richiesta'
        : isWaitingForPhaseConfirmation
            ? 'Spedita dal laboratorio · attesa conferma prova'
            : prescription.stato
                ? getStatusLabel(prescription.stato)
                : 'In attesa'
    const hasSyncFailure =
        primoUpSyncStatus === 'failed' ||
        prescription.feedback_sync_status === 'failed' ||
        prescription.phase_sync_status === 'failed'
    const hasSyncUncertain = primoUpSyncStatus === 'uncertain'
    const hasSyncPending =
        primoUpSyncStatus === 'queued' ||
        prescription.feedback_sync_status === 'pending' ||
        prescription.feedback_sync_status === 'processing' ||
        prescription.phase_sync_status === 'pending' ||
        prescription.phase_sync_status === 'processing'
    const syncState = hasSyncUncertain
        ? { label: 'Esito PrimoUp da verificare', tone: 'text-orange-800', dot: 'bg-orange-500' }
        : hasSyncFailure
        ? { label: 'Sincronizzazione non riuscita', tone: 'text-red-700', dot: 'bg-red-500' }
        : hasSyncPending
            ? { label: 'Sincronizzazione in corso', tone: 'text-blue-700', dot: 'bg-blue-500' }
            : prescription.phase_skip_status === 'pending'
                ? { label: 'In attesa del laboratorio', tone: 'text-amber-700', dot: 'bg-amber-500' }
                : prescription.external_prescription_id || prescription.external_number_text
                    ? { label: 'Sincronizzato con PrimoUp', tone: 'text-emerald-700', dot: 'bg-emerald-500' }
                    : { label: 'Non ancora sincronizzato', tone: 'text-gray-700', dot: 'bg-gray-400' }
    const nextCaseStep = prescription.test_change_pending ? 'Consulta la richiesta di cambio prova qui sotto' : prescription.iso_action_pending ? 'Gestisci la richiesta ISO qui sotto' : prescription.phase_skip_status === 'pending'
        ? canManagePhasePolicies
            ? (isPartialGroupSync ? 'Riprova gli elementi mancanti' : 'Decidi il salto di fase')
            : 'Attendi la decisione del laboratorio'
        : hasSyncUncertain
            ? 'Verifica la prescrizione in PrimoUp o contatta l’assistenza'
        : requiresNewPhase && canEditNotes
            ? 'Seleziona una nuova fase'
            : prescription.feedback_sync_status === 'failed' && canEditNotes
                ? 'Riprova il feedback'
                : prescription.phase_sync_status === 'failed'
                    ? canRetryPhase ? 'Riprova la nuova fase' : 'Contatta un amministratore per riprovare l’invio'
                    : prescription.stato === 'spedita' &&
                      prescription.attesa_prossima_prova === true &&
                      canConfirmCurrentPhase
                        ? 'Conferma la prova ricevuta'
                        : hasRecordedFeedbackForWaitingPhase
                            ? 'Verifica la conferma già registrata'
                        : prescription.stato === 'spedita' &&
                          prescription.attesa_prossima_prova !== true &&
                          prescription.feedback_sync_status !== 'pending' &&
                          prescription.feedback_sync_status !== 'processing' &&
                          canEditNotes
                            ? 'Registra il feedback'
                            : canEditNotes &&
                              !prescription.external_number_text &&
                              prescription.stato === 'creata' &&
                              primoUpSyncStatus !== 'queued' &&
                              primoUpSyncStatus !== 'uncertain'
                                ? (primoUpSyncStatus === 'failed' ? 'Riprova l’invio a PrimoUp' : 'Invia a PrimoUp')
                                : prescription.feedback_sync_status === 'pending' ||
                                  prescription.feedback_sync_status === 'processing' ||
                                  prescription.phase_sync_status === 'pending' ||
                                  prescription.phase_sync_status === 'processing'
                                    ? 'Attendi il completamento della sincronizzazione'
                                    : 'Nessuna attività segnalata'
    
    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-6">
                <Button
                    variant="outline"
                    onClick={() => navigate('/prescriptions')}
                    className="mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Torna alle prescrizioni
                </Button>

                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <h1 className="text-3xl font-bold tracking-[-0.02em] text-gray-900">
                        Dettagli prescrizione #{prescriptionDisplayNumber(prescription, 'Bozza · interno')}
                    </h1>

                    <div className="flex flex-wrap gap-2">
                        {hasPermission('use_prescription_chat') && prescriptionId && (
                            <Button
                                variant="outline"
                                onClick={() => openChat(prescriptionId)}
                            >
                                <MessagesSquare className="w-4 h-4 mr-2" />
                                Chat
                            </Button>
                        )}

                        {/* Feedback button for shipped prescriptions */}
                        {canEditNotes && !prescription.iso_action_pending && !prescription.test_change_pending &&
                            prescription.stato === 'spedita' &&
                            prescription.attesa_prossima_prova !== true &&
                            prescription.feedback_sync_status !== 'pending' &&
                            prescription.feedback_sync_status !== 'processing' && (
                            <Button
                                onClick={handleOpenFeedback}
                                variant="default"
                                className="bg-green-600 hover:bg-green-700"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Feedback
                            </Button>
                        )}

                        {/* Sync to PrimoUp button - only show if not already synced */}
                        {canEditNotes &&
                            !prescription?.external_number_text &&
                            prescription.stato === 'creata' &&
                            prescription.phase_skip_status !== 'pending' &&
                            primoUpSyncStatus !== 'queued' &&
                            primoUpSyncStatus !== 'uncertain' && (
                            <Button
                                onClick={handleSyncToPrimoUp}
                                disabled={isSyncing}
                                variant="default"
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                {isSyncing ? (
                                    <>
                                        <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        Sincronizzando...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4 mr-2" />
                                        {primoUpSyncStatus === 'failed'
                                            ? 'Riprova invio a PrimoUp'
                                            : 'Invia a PrimoUp'}
                                    </>
                                )}
                            </Button>
                        )}

                        {primoUpSyncStatus === 'queued' && (
                            <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-md border border-blue-200">
                                <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                                <span className="text-sm font-medium">
                                    Invio a PrimoUp in corso
                                </span>
                            </div>
                        )}

                        {primoUpSyncStatus === 'uncertain' && (
                            <div className="max-w-xl rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-950">
                                <p className="font-semibold">Non reinviare automaticamente</p>
                                <p className="mt-0.5">
                                    {prescription.lab_api_last_response?.error ??
                                        'Verifica in PrimoUp se la prescrizione è stata creata, poi contatta l’assistenza.'}
                                </p>
                            </div>
                        )}

                        {canEditGeneralInfo && (
                            <>
                                {!isEditing ? (
                                    <Button onClick={handleEdit}>
                                        <Edit className="w-4 h-4 mr-2" />
                                        Modifica
                                    </Button>
                                ) : (
                                    <>
                                        <Button onClick={handleSave} variant="default">
                                            <Save className="w-4 h-4 mr-2" />
                                            Salva
                                        </Button>
                                        <Button onClick={handleCancel} variant="outline">
                                            <X className="w-4 h-4 mr-2" />
                                            Annulla
                                        </Button>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <section
                aria-labelledby="case-state-heading"
                className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_10px_32px_rgba(15,23,42,0.05)]"
            >
                <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 id="case-state-heading" className="text-lg font-semibold tracking-[-0.02em] text-gray-950">Stato del caso</h2>
                        <p className="mt-1 text-sm text-gray-500">Quadro operativo della prescrizione e del collegamento con PrimoUp.</p>
                    </div>
                    <Badge
                        className={
                            requiresNewPhase || isWaitingForPhaseConfirmation || prescription.test_change_pending
                                ? 'bg-amber-100 text-amber-900'
                                : getStatusColor(prescription.stato || 'pending')
                        }
                    >
                        {displayedCaseStatus}
                    </Badge>
                </div>
                <div className="grid md:grid-cols-3">
                    <div className="px-5 py-5 md:border-r md:border-gray-100">
                        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Fase corrente</p>
                        <p className="mt-2 font-medium text-gray-950">
                            {currentPhaseRow ? displayPhaseName(currentPhaseRow) || 'Fase non specificata' : displayedCaseStatus}
                        </p>
                        <p className="mt-1 text-sm text-gray-500">Ultimo aggiornamento: {formatDate(prescription.updated_at)}</p>
                    </div>
                    <div className="border-t border-gray-100 px-5 py-5 md:border-r md:border-t-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Sincronizzazione</p>
                        <div className="mt-2 flex items-center gap-2">
                            <span className={`h-2.5 w-2.5 rounded-full ${syncState.dot}`} aria-hidden="true" />
                            <p className={`font-medium ${syncState.tone}`}>{syncState.label}</p>
                        </div>
                        {prescription.external_number_text && <p className="mt-1 text-sm text-gray-500">Numero PrimoUp: {prescription.external_number_text}</p>}
                    </div>
                    <div className="border-t border-gray-100 px-5 py-5 md:border-t-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">Prossimo passo</p>
                        <p className="mt-2 font-medium text-gray-950">{nextCaseStep}</p>
                    </div>
                </div>
            </section>

            <TestChangeRequestPanel
                key={`${prescriptionId}:${searchParams.get('testChange') ?? ''}:${searchParams.get('testChangeDecision') ?? ''}`}
                prescriptionId={prescriptionId}
                requestId={searchParams.get('testChange')}
                suggestedDecision={searchParams.get('testChangeDecision')}
                channel={searchParams.has('testChangeDecision') ? 'email' : 'prescription'}
                className="mb-6"
            />

            {prescription.iso_action_pending && <div className="mb-6"><IsoActionPanel key={prescriptionId} prescriptionId={prescriptionId} autoOpen={searchParams.has('iso')} /></div>}

            {canConfirmCurrentPhase && (
                <div
                    className="mb-6 flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between"
                    role="status"
                    aria-live="polite"
                >
                    <div>
                        <p className="font-semibold text-amber-950">Conferma la prova ricevuta</p>
                        <p className="mt-1 text-sm text-amber-900">
                            Il laboratorio ha spedito una prova intermedia. Indica se è conforme per sbloccare il passaggio successivo.
                        </p>
                    </div>
                    <Button
                        type="button"
                        className="shrink-0 bg-amber-600 text-white hover:bg-amber-700"
                        onClick={() => {
                            attesaModalDismissedRef.current = false
                            setShowAttesaProvaModal(true)
                        }}
                    >
                        <ClipboardCheck className="mr-2 h-4 w-4" />
                        Conferma prova ricevuta
                    </Button>
                </div>
            )}

            {hasRecordedFeedbackForWaitingPhase && canEditNotes && (
                <div
                    className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950"
                    role="status"
                >
                    <p className="font-semibold">Conferma già registrata per questa fase</p>
                    <p className="mt-1 text-sm">
                        La prescrizione risulta ancora in attesa. Controlla la sincronizzazione del feedback prima di registrare un’altra decisione.
                    </p>
                </div>
            )}

            {prescription.phase_skip_status === 'pending' && (
                <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950">
                    <p className="font-semibold">
                        {isPartialGroupSync
                            ? 'Gruppo parzialmente sincronizzato'
                            : 'Salto di fase in attesa del laboratorio'}
                    </p>
                    <p className="mt-1 text-sm">
                        {isPartialGroupSync
                            ? `${syncedGroupMembers} di ${groupMembers?.length ?? 0} elementi sono già su PrimoUp. Il retry riguarda soltanto i ${pendingGroupMembers} mancanti.`
                            : `Fasi saltate: ${prescription.phase_skip_skipped_phases?.map((phase: any) => phase.name).join(', ') || 'richiesta di gruppo'}. PrimoUp non è stato chiamato.`}
                    </p>
                    {canManagePhasePolicies && (
                        <div className="mt-3 flex gap-2">
                            <Button
                                type="button"
                                onClick={() => void handlePhaseDecision('approve')}
                                disabled={phaseDecisionLoading}
                            >
                                {isPartialGroupSync ? 'Riprova elementi mancanti' : 'Approva e sincronizza'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => void handlePhaseDecision('reject')}
                                disabled={phaseDecisionLoading}
                                className="border-red-300 text-red-700"
                            >
                                Rifiuta
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {(prescription.feedback_sync_status === 'pending' ||
                prescription.feedback_sync_status === 'processing') && (
                <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                    <p className="font-semibold">Feedback in sincronizzazione</p>
                    <p className="mt-1">
                        Il server sta aggiornando PrimoUp. Puoi lasciare questa pagina: il processo continuerà in background.
                    </p>
                </div>
            )}

            {prescription.feedback_sync_status === 'failed' && canEditNotes && (
                <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900">
                    <p className="font-semibold">Feedback non sincronizzato con PrimoUp</p>
                    <p className="mt-1">
                        La decisione è salvata in app e non verrà duplicata. Riprova la sola sincronizzazione esterna.
                    </p>
                    <Button className="mt-3" variant="outline" onClick={() => void handleRetryFeedbackSync()}>
                        Riprova feedback
                    </Button>
                </div>
            )}

            {(prescription.phase_sync_status === 'pending' ||
                prescription.phase_sync_status === 'processing') && (
                <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                    <p className="font-semibold">Nuova fase in sincronizzazione</p>
                    <p className="mt-1">La fase è salvata e PrimoUp viene aggiornato dal server.</p>
                </div>
            )}

            {prescription.phase_sync_status === 'failed' && (
                <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900">
                    <p className="font-semibold">Nuova fase non sincronizzata con PrimoUp</p>
                    <p className="mt-1">{canRetryPhase
                        ? 'Controlla data, ora e modalità impronta prima di riprovare l’invio a PrimoUp.'
                        : 'Contatta un amministratore per aggiornare i dati di consegna e riprovare l’invio.'}</p>
                    {canRetryPhase && currentPhaseRow && (showPhaseRetryForm ? (
                        <PhaseRetryForm key={currentPhaseRow._id} row={currentPhaseRow} onSubmit={handleRetryPhaseSync}
                            onCancel={() => setShowPhaseRetryForm(false)} />
                    ) : (
                        <Button className="mt-3" variant="outline" onClick={() => setShowPhaseRetryForm(true)}>
                            Riprova fase
                        </Button>
                    ))}
                </div>
            )}

            {!prescription.iso_action_pending && prescription.chiusura_automatica_consegna && prescription.stato === 'completata' && (
                <div className="mb-6 p-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 text-sm">
                    <p className="font-medium">Prova Consegna: chiusura automatica dal laboratorio</p>
                    <p className="mt-1">
                        Non è previsto il feedback sui singoli denti. Se riscontri problemi con il lavoro o con la
                        fatturazione, usa la sezione contabilità qui sotto per la segnalazione.
                    </p>
                </div>
            )}

            {!prescription.iso_action_pending && ((prescription as any).richiede_nuova_prova_post_esclusione === true ||
                (prescription as any).richiede_nuova_prova_post_rifiuto_fase === true ||
                (prescription as any).richiede_nuova_prova_post_feedback === true) && canEditNotes && (
                <div className="mb-6 p-4 rounded-lg border border-red-200 bg-red-50 text-red-900 text-sm">
                    <p className="font-semibold">
                        {(prescription as any).richiede_nuova_prova_post_rifiuto_fase === true
                            ? 'Salto di fase rifiutato — scegli una nuova fase'
                            : (prescription as any).richiede_nuova_prova_post_feedback === true
                                ? 'Feedback registrato — scegli la fase successiva'
                            : 'Esclusione contabilità approvata — nuova prova richiesta'}
                    </p>
                    <p className="mt-1">
                        {(prescription as any).richiede_nuova_prova_post_rifiuto_fase === true
                            ? 'Il laboratorio non ha approvato il salto richiesto. Seleziona una fase alternativa per proseguire.'
                            : (prescription as any).richiede_nuova_prova_post_feedback === true
                                ? 'PrimoUp è allineato. Seleziona la fase immediatamente successiva oppure un salto consentito.'
                            : 'L\'esclusione dal cedolino è stata approvata. È necessario selezionare una nuova prova per questa prescrizione.'}
                    </p>
                    <Button
                        className="mt-3"
                        onClick={() => setShowNewProvaModal(true)}
                    >
                        Seleziona nuova prova
                    </Button>
                </div>
            )}

            {prescription.stato === 'completata' && prescription.external_prescription_id && canEditNotes && (
                <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <p className="font-medium text-gray-900">Dichiarazione di conformità</p>
                        <p className="text-sm text-gray-600 mt-1">
                            Scarica o apri la dichiarazione generata su PrimoUp per questa prescrizione.
                        </p>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={conformityLoading}
                        onClick={() => void handleConformityDocument()}
                        className="shrink-0"
                    >
                        {conformityLoading ? (
                            <span className="flex items-center gap-2">
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                                Attendere…
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <FileBadge className="h-4 w-4" />
                                Richiedi dichiarazione di conformità
                            </span>
                        )}
                    </Button>
                </div>
            )}

            {/* Sync Error Message */}
            {syncError && (
                <div className={`mb-6 p-5 rounded-lg border-2 ${
                    syncError.includes('504') 
                        ? 'bg-amber-50 border-amber-300'
                        : syncError.includes('422')
                        ? 'bg-orange-50 border-orange-300'
                        : 'bg-red-50 border-red-300'
                }`}>
                    <div className="flex items-start">
                        <AlertCircle className={`w-6 h-6 mt-0.5 mr-3 flex-shrink-0 ${
                            syncError.includes('504') 
                                ? 'text-amber-600'
                                : syncError.includes('422')
                                ? 'text-orange-600'
                                : 'text-red-600'
                        }`} />
                        <div className="flex-1">
                            <h3 className={`text-base font-semibold mb-2 ${
                                syncError.includes('504') 
                                    ? 'text-amber-900'
                                    : syncError.includes('422')
                                    ? 'text-orange-900'
                                    : 'text-red-900'
                            }`}>
                                {syncError.includes('504') 
                                    ? 'Timeout del Server (504)'
                                    : syncError.includes('422')
                                    ? 'Dati Non Validi (422)'
                                    : 'Errore di Sincronizzazione'}
                            </h3>
                            <p className={`text-sm leading-relaxed whitespace-pre-wrap ${
                                syncError.includes('504') 
                                    ? 'text-amber-800'
                                    : syncError.includes('422')
                                    ? 'text-orange-800'
                                    : 'text-red-800'
                            }`}>
                                {syncError}
                            </p>
                            {syncError.includes('504') && (
                                <div className="mt-3 pt-3 border-t border-amber-200">
                                    <p className="text-sm font-medium text-amber-900">
                                        💡 Suggerimenti:
                                    </p>
                                    <ul className="mt-2 space-y-1 text-sm text-amber-800">
                                        <li>• Attendi almeno 1-2 minuti prima di riprovare</li>
                                        <li>• Verifica che la prescrizione non sia già stata sincronizzata</li>
                                    </ul>
                                </div>
                            )}
                            {syncError.includes('422') && (
                                <div className="mt-3 pt-3 border-t border-orange-200">
                                    <p className="text-sm font-medium text-orange-900">
                                        💡 Suggerimenti:
                                    </p>
                                    <ul className="mt-2 space-y-1 text-sm text-orange-800">
                                        <li>• Verifica che tutti i campi obbligatori siano compilati correttamente</li>
                                        <li>• Controlla che la data di consegna sia nel futuro</li>
                                        <li>• Se il problema persiste contatta gli amministratori</li>
                                    </ul>
                                </div>
                            )}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSyncError(null)}
                            className={`ml-3 flex-shrink-0 ${
                                syncError.includes('504') 
                                    ? 'text-amber-600 hover:text-amber-800 hover:bg-amber-100'
                                    : syncError.includes('422')
                                    ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-100'
                                    : 'text-red-600 hover:text-red-800 hover:bg-red-100'
                            }`}
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Info */}
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Informazioni Generali</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {isEditing && (
                                    <div>
                                        <label htmlFor="prescription-status" className="text-sm font-medium text-gray-500">Stato</label>
                                        <select
                                            id="prescription-status"
                                            value={editStatus}
                                            onChange={(e) => setEditStatus(e.target.value)}
                                            disabled={
                                                !['creata', 'inviata', 'in_lavorazione'].includes(
                                                    prescription.stato
                                                )
                                            }
                                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value={prescription.stato}>
                                                {getStatusLabel(prescription.stato)}
                                            </option>
                                            {prescription.stato === 'inviata' && (
                                                <option value="in_lavorazione">In lavorazione</option>
                                            )}
                                            {prescription.stato === 'in_lavorazione' && (
                                                <option value="spedita">Spedita dal laboratorio</option>
                                            )}
                                            {['creata', 'inviata', 'in_lavorazione'].includes(
                                                prescription.stato
                                            ) && <option value="annullata">Annullata</option>}
                                        </select>
                                    </div>
                                )}

                                {hasPermission('view_prices') && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Prezzo totale</label>
                                        {isEditing ? (
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={editPrice}
                                                onChange={(e) => setEditPrice(e.target.value)}
                                                className="mt-1"
                                                placeholder="0.00"
                                            />
                                        ) : (
                                            <div className="mt-1 text-lg font-semibold">
                                                {formatCurrency(prescription.PrezzoNetto ?? 0)}
                                            </div>
                                        )}
                                        {canRepairPricing && pricingPreview?.mismatch && (
                                            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-950">
                                                <p>
                                                    Prezzo non allineato all&apos;odontogramma:{' '}
                                                    <strong>
                                                        {pricingPreview.applicationKind}
                                                    </strong>{' '}
                                                    ({pricingPreview.billableCount} elementi) →{' '}
                                                    <strong>{formatCurrency(pricingPreview.expectedPrice)}</strong>
                                                </p>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className="mt-2"
                                                    disabled={recalculatingPrice}
                                                    onClick={() => void handleRecalculatePrice()}
                                                >
                                                    {recalculatingPrice
                                                        ? 'Aggiornamento…'
                                                        : 'Allinea prezzo contabilità'}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label className="text-sm font-medium text-gray-500">
                                        {isEditing ? 'Numero interno PrimoFastLab' : 'Numero prescrizione PrimoUp'}
                                    </label>
                                    {isEditing ? (
                                        <Input
                                            type="text"
                                            value={editPrescriptionNumber}
                                            onChange={(e) => setEditPrescriptionNumber(e.target.value)}
                                            className="mt-1"
                                            placeholder="Numero interno PrimoFastLab"
                                        />
                                    ) : (
                                        <div className="mt-1">
                                            {prescriptionDisplayNumber(prescription, 'Bozza · interno')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Paziente</label>
                                    {isEditing && canEditAllGeneralInfo ? (
                                        <Input
                                            type="text"
                                            value={editPatientName}
                                            onChange={(e) => setEditPatientName(e.target.value)}
                                            className="mt-1"
                                            placeholder="Nome paziente"
                                        />
                                    ) : (
                                        <div className="mt-1">{prescription.NomePaziente}</div>
                                    )}
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Medico</label>
                                    <div className="mt-1">
                                        {prescription.medico ?
                                            `${prescription.medico.nome} ${prescription.medico.cognome}` :
                                            'Non specificato'
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Prestazione</label>
                                    <div className="mt-1 font-medium text-gray-900">
                                        {prescription.service?.nome_visualizzato ||
                                            prescription.service?.nome_prestazione ||
                                            'Non specificata'}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Quantità</label>
                                    <div className="mt-1">
                                        {persistedSelectionInfo.pricingMode === 'per_tooth'
                                            ? `${persistedSelectionInfo.billableCount} ${persistedSelectionInfo.billableCount === 1 ? 'elemento' : 'elementi'} (uno per dente)`
                                            : persistedSelectionInfo.pricingMode === 'per_mouth'
                                                ? '1 prestazione per bocca'
                                            : `${persistedSelectionInfo.billableCount} ${persistedSelectionInfo.billableCount === 1 ? 'arcata' : 'arcate'}`}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Azienda</label>
                                    <div className="mt-1">{prescription.company?.nome || 'Non specificata'}</div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Creato da</label>
                                    <div className="mt-1">
                                        {prescription.profile ?
                                            `${prescription.profile.nome} ${prescription.profile.cognome}` :
                                            'Utente sconosciuto'
                                        }
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Data Creazione</label>
                                    <div className="mt-1">{formatDate(prescription.created_at)}</div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Ultimo Aggiornamento</label>
                                    <div className="mt-1">{formatDate(prescription.updated_at)}</div>
                                </div>
                            </div>

                            {/* Tinta e Data Prescrizione */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Tinta</label>
                                    {isEditing && canEditAllGeneralInfo ? (
                                        <select
                                            value={editTint}
                                            onChange={(e) => {
                                                setEditTint(e.target.value)
                                                const selectedColor = colors?.find((c) => c.code === e.target.value)
                                                setEditColorId(selectedColor?.id || 0)
                                            }}
                                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                            disabled={colorsLoading}
                                        >
                                            <option value="">{colorsLoading ? 'Caricamento tinte...' : 'Seleziona tinta'}</option>
                                            {colors?.map((color, index) => (
                                                <option key={index} value={color.code || color.id || color}>
                                                    {color.code || color.id || color}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div className="mt-1">
                                            {currentPhaseRow?.tinta || 'Non specificata'}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-500">Data e Ora Consegna Prevista</label>
                                    <div className="mt-1">
                                        {isEditing && canEditAllGeneralInfo ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                <Input
                                                    type="date"
                                                    value={editDeliveryDate}
                                                    onChange={(e) => setEditDeliveryDate(e.target.value)}
                                                    min={minDateString}
                                                />
                                                <Input
                                                    type="time"
                                                    value={editDeliveryTime}
                                                    onChange={(e) => setEditDeliveryTime(e.target.value)}
                                                    min={editDeliveryDate === minDateString ? minTimeString : undefined}
                                                />
                                            </div>
                                        ) : currentPhaseRow?.dataapp ? (
                                                <div>{formatDate(currentPhaseRow.dataapp)}</div>
                                        ) : 'Non specificata'}
                                    </div>
                                </div>
                            </div>

                            {/* Denti e Impianti */}
                            {(() => {
                                const selectionInfo = isEditing && canEditAllGeneralInfo
                                    ? getSelectedElementsInfo(editSelectedTeeth, editSelectedImplants)
                                    : getSelectedElementsInfo();
                                return (
                                    <div className={`grid gap-4 ${isEditing && canEditAllGeneralInfo ? 'grid-cols-1' : 'grid-cols-2'}`}>
                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Elementi Selezionati</label>
                                            <div className="mt-1">
                                                {isEditing && canEditAllGeneralInfo ? (
                                                    <Odontogram
                                                        companyId={prescription.company_id as Id<'companies'>}
                                                        selectedTeeth={editSelectedTeeth}
                                                        selectedImplants={editSelectedImplants}
                                                        implantTypes={editImplantTypes}
                                                        onSelectionChange={(teeth, implants, types) => {
                                                            setEditSelectedTeeth(teeth)
                                                            setEditSelectedImplants(implants)
                                                            setEditImplantTypes(types)
                                                        }}
                                                        pricingMode={resolveServicePricingMode(prescription.service)}
                                                        showValidationErrors={editSelectionAttempted}
                                                    />
                                                ) : (
                                                    <div className="text-sm text-gray-900 break-words whitespace-pre-line">
                                                        {selectionInfo.selectedElements.join('\n')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium text-gray-500">Tipo Applicazione</label>
                                            <div className="mt-1">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        {selectionInfo.description}
                                                    </span>
                                                </div>
                                            </div>
                                    </div>
                                );
                            })()}

                            {/* Notes Section */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-500">Note</label>
                                    {canEditNotes && !isEditingNotes && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleEditNotes}
                                        >
                                            <Edit className="w-4 h-4 mr-1" />
                                            {currentPhaseRow?.note ? 'Modifica' : 'Aggiungi'}
                                        </Button>
                                    )}
                                </div>

                                {isEditingNotes ? (
                                    <div className="space-y-2">
                                        <textarea
                                            value={editNotes}
                                            onChange={(e) => setEditNotes(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                            rows={4}
                                            placeholder="Inserisci le note per questa prescrizione..."
                                        />
                                        <div className="flex space-x-2">
                                            <Button
                                                variant="default"
                                                size="sm"
                                                onClick={handleSaveNotes}
                                            >
                                                <Save className="w-4 h-4 mr-1" />
                                                Salva
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleCancelNotes}
                                            >
                                                <X className="w-4 h-4 mr-1" />
                                                Annulla
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-1 p-3 bg-gray-50 rounded-md whitespace-pre-wrap break-words overflow-wrap-break-word">
                                        {currentPhaseRow?.note || (
                                            <span className="text-gray-400 italic">
                                                {canEditNotes ? 'Nessuna nota presente. Clicca "Aggiungi" per aggiungerne una.' : 'Nessuna nota presente.'}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {prescription.prescription_group_id &&
                        groupMembers &&
                        groupMembers.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Ordine composto (stesso gruppo)</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="text-sm space-y-2">
                                        {groupMembers.map((m: any) => (
                                            <li key={m._id}>
                                                <a
                                                    href={`/prescriptions/${m._id}`}
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    {prescriptionDisplayNumber(m, 'Bozza · interno')}{' '}
                                                    (parte {m.group_index ?? '?'})
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        )}

                    {prescription.stato === 'completata' &&
                        hasPermission('segnala_contabilita_problema') &&
                        !(prescription as any).contabilita_esclusione_richiesta &&
                        !(prescription as any).contabilita_esclusa &&
                        !(
                            (prescription as any).feedback_sync_kind === 'accounting_exclusion' &&
                            ['pending', 'processing', 'failed'].includes(
                                (prescription as any).feedback_sync_status
                            )
                        ) && (() => {
                            let teeth: string[] = []
                            try { teeth = prescription.denti_selezionati ? JSON.parse(prescription.denti_selezionati) : [] } catch { /* */ }
                            const options: { label: string; value: string }[] = teeth.map((t: string) => ({
                                label: `Dente ${t}`,
                                value: t,
                            }))
                            const toggleElement = (val: string) => {
                                setExclusionElements(prev =>
                                    prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                )
                            }
                            return (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Segnalazione contabilità</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <p className="text-sm text-gray-600">
                                        Seleziona i denti che richiedono esclusione dal cedolino. Gli impianti non sono fatturati e non rientrano nella segnalazione.
                                    </p>
                                    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                        Nota: i denti servono a descrivere il problema; per ora, se il laboratorio approva, viene rimossa dalla contabilità l'intera prescrizione.
                                    </p>
                                    {options.length > 0 ? (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {options.map(opt => (
                                                <label
                                                    key={opt.value}
                                                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${
                                                        exclusionElements.includes(opt.value)
                                                            ? 'border-primary bg-primary/5 text-primary font-medium'
                                                            : 'border-gray-200 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="rounded border-gray-300"
                                                        checked={exclusionElements.includes(opt.value)}
                                                        onChange={() => toggleElement(opt.value)}
                                                    />
                                                    {opt.label}
                                                </label>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400">Nessun elemento disponibile.</p>
                                    )}
                                    <textarea
                                        className="w-full border rounded-md p-2 text-sm"
                                        rows={2}
                                        value={exclusionNote}
                                        onChange={(e) => setExclusionNote(e.target.value)}
                                        placeholder="Note aggiuntive (opzionale)"
                                    />
                                    <Button
                                        disabled={exclusionElements.length === 0}
                                        onClick={async () => {
                                            if (exclusionElements.length === 0) return
                                            await requestContabilitaExclusion({
                                                prescription_id: prescriptionId,
                                                tooth_numbers: exclusionElements,
                                                note: exclusionNote.trim() || undefined,
                                            })
                                            setExclusionElements([])
                                            setExclusionNote('')
                                        }}
                                    >
                                        Richiedi rimozione da contabilità
                                    </Button>
                                </CardContent>
                            </Card>
                            )
                        })()}

                    {/* Prescription phases */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Fasi della prescrizione</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {currentPhaseRow ? (
                                <>
                                    <section aria-labelledby="current-phase-heading">
                                        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primoup-700">
                                            Fase corrente
                                        </p>
                                        <div className="mt-2 rounded-xl border border-primoup-200 bg-primoup-50/60 p-4 sm:p-5">
                                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                                <div className="min-w-0">
                                                    <h3 id="current-phase-heading" className="text-base font-semibold text-gray-950">
                                                        {displayPhaseName(currentPhaseRow) || 'Fase non specificata'}
                                                    </h3>
                                                    <time
                                                        dateTime={new Date(currentPhaseRow.created_at).toISOString()}
                                                        className="mt-1 block text-xs text-gray-500"
                                                    >
                                                        Avviata il {formatDate(currentPhaseRow.created_at)}
                                                    </time>
                                                </div>
                                                {hasPermission('view_prices') && typeof currentPhaseRow.prezzo === 'number' && (
                                                    <span className="shrink-0 text-sm font-semibold text-gray-900">
                                                        {formatCurrency(currentPhaseRow.prezzo)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
                                                {currentPhaseRow.scansione_intraorale && <span>✓ Scansione intraorale</span>}
                                                {currentPhaseRow.chiama_medico && <span>✓ Chiama il medico</span>}
                                                {currentPhaseRow.presa_fisica_corriere && <span>✓ Presa fisica corriere</span>}
                                            </div>
                                        </div>
                                    </section>

                                    {pastPhaseRows.length > 0 && (
                                        <section aria-labelledby="past-phases-heading" className="border-t border-gray-100 pt-5">
                                            <h3 id="past-phases-heading" className="text-sm font-semibold text-gray-900">
                                                Fasi passate
                                            </h3>
                                            <div className="mt-3 space-y-2">
                                                {pastPhaseRows.map((row: any) => (
                                                    <article key={row._id} className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
                                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                                            <div className="min-w-0">
                                                                <h4 className="font-medium text-gray-900">
                                                                    {displayPhaseName(row) || 'Fase non specificata'}
                                                                </h4>
                                                                <time
                                                                    dateTime={new Date(row.created_at).toISOString()}
                                                                    className="mt-1 block text-xs text-gray-500"
                                                                >
                                                                    Avviata il {formatDate(row.created_at)}
                                                                </time>
                                                            </div>
                                                            {hasPermission('view_prices') && typeof row.prezzo === 'number' && (
                                                                <span className="shrink-0 text-sm font-medium text-gray-700">
                                                                    {formatCurrency(row.prezzo)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                                                            {row.scansione_intraorale && <span>✓ Scansione intraorale</span>}
                                                            {row.chiama_medico && <span>✓ Chiama il medico</span>}
                                                            {row.presa_fisica_corriere && <span>✓ Presa fisica corriere</span>}
                                                        </div>
                                                    </article>
                                                ))}
                                            </div>
                                        </section>
                                    )}
                                </>
                            ) : (
                                <div className="py-6 text-center text-sm text-gray-500">
                                    Nessuna fase registrata
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* File Attachments */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Allegati</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {attachmentError && (
                                <div
                                    role="alert"
                                    className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-950"
                                >
                                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                                    <span className="min-w-0 break-words">{attachmentError}</span>
                                </div>
                            )}
                            {attachmentNotice && (
                                <div
                                    role="status"
                                    className="mb-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-950"
                                >
                                    <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                                    <span>{attachmentNotice}</span>
                                </div>
                            )}
                            {canEditNotes && (
                                <div className="mb-4">
                                    <div className="flex flex-col gap-2">
                                        <input
                                            id="detail-attachments"
                                            type="file"
                                            multiple
                                            accept={PRIMOUP_ATTACHMENT_ACCEPT}
                                            onChange={(e) => {
                                                const files = Array.from(e.target.files || [])
                                                const validFiles = files.filter((file) =>
                                                    !getPrimoUpAttachmentValidationError({
                                                        filename: file.name,
                                                        contentType: file.type,
                                                    })
                                                )
                                                const invalidFiles = files.filter((file) => !validFiles.includes(file))
                                                setSelectedFiles(validFiles)
                                                setAttachmentNotice(null)
                                                setAttachmentError(
                                                    invalidFiles.length > 0
                                                        ? `Puoi caricare soltanto JPG, JPEG o PNG. File esclusi: ${invalidFiles.map((file) => file.name).join(', ')}.`
                                                        : null
                                                )
                                                e.target.value = ''
                                            }}
                                            className="peer sr-only"
                                            disabled={uploadingFiles}
                                        />
                                        <label
                                            htmlFor="detail-attachments"
                                            className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 peer-focus-visible:outline-hidden peer-focus-visible:ring-2 peer-focus-visible:ring-primoup-500 peer-focus-visible:ring-offset-2"
                                        >
                                            <FileBadge className="h-4 w-4" aria-hidden="true" />
                                            Scegli allegati
                                        </label>
                                        <span className="text-xs text-gray-500">
                                            {selectedFiles.length > 0
                                                ? `${selectedFiles.length} file selezionati`
                                                : 'Nessun file selezionato'}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Formati supportati da PrimoUp: JPG, JPEG e PNG.
                                    </p>

                                    {selectedFiles.length > 0 && (
                                        <div className="mt-2">
                                            <Button
                                                onClick={handleFileUpload}
                                                disabled={uploadingFiles}
                                                className="w-full"
                                            >
                                                <Upload className="w-4 h-4 mr-2" />
                                                {uploadingFiles ? 'Caricamento...' : `Carica ${selectedFiles.length} file`}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="space-y-2">
                                {prescription.attachments && prescription.attachments.length > 0 ? (
                                    prescription.attachments.map((attachment:any) => {
                                        const sync = attachmentSyncPresentation(attachment)
                                        const canRetry = canEditNotes &&
                                            !getPrimoUpAttachmentValidationError({
                                                filename: attachment.filename,
                                                contentType: attachment.content_type,
                                            }) && (
                                                attachment.primoup_sync_status === 'failed' ||
                                                attachment.primoup_sync_status === undefined
                                            )
                                        const canDelete = canEditNotes && ![
                                            'synced',
                                            'processing',
                                            'uncertain',
                                        ].includes(attachment.primoup_sync_status)
                                        return (
                                        <div key={attachment._id} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                                            <div className="min-w-0">
                                                <div className="break-words text-sm font-medium text-gray-900">{attachment.filename}</div>
                                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                                    <span className="text-xs text-gray-500">
                                                        {(attachment.file_size / 1024).toFixed(1)} KB
                                                    </span>
                                                    <Badge variant="outline" className={sync.className}>
                                                        {sync.label}
                                                    </Badge>
                                                </div>
                                                {sync.description && (
                                                    <p className="mt-1 break-words text-xs leading-4 text-gray-600">
                                                        {sync.description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {canRetry && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => void handleRetryAttachmentSync(attachment._id)}
                                                        disabled={retryingAttachmentId === String(attachment._id)}
                                                    >
                                                        <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                                                        {retryingAttachmentId === String(attachment._id)
                                                            ? 'Invio…'
                                                            : 'Invia a PrimoUp'}
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleDownloadAttachment(attachment.file_id, attachment.filename)}
                                                    aria-label={`Scarica ${attachment.filename}`}
                                                >
                                                    <Download className="w-3 h-3" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setShareDialogAttachment(attachment)}
                                                    aria-label={`Genera link condiviso per ${attachment.filename}`}
                                                >
                                                    <Link2 className="w-3 h-3" />
                                                </Button>
                                                {canDelete && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleDeleteAttachment(attachment._id)}
                                                        aria-label={`Elimina ${attachment.filename}`}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        )
                                    })
                                ) : (
                                    <div className="text-gray-500 text-center py-4 text-sm">
                                        Nessun allegato
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {showAttesaProvaModal &&
                canEditNotes &&
                prescription.stato === 'spedita' &&
                prescription.attesa_prossima_prova === true &&
                canConfirmCurrentPhase && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Conferma prova ricevuta</h2>
                        <p className="text-sm text-gray-600 mb-6">
                            Il laboratorio ha completato e spedito questa prova (non è una Consegna finale). Il lavoro
                            ricevuto è conforme alle attese?
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    attesaModalDismissedRef.current = true
                                    setShowAttesaProvaModal(false)
                                }}
                            >
                                Più tardi
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => void handleAttesaProvaNonConforme()}
                            >
                                No, non conforme
                            </Button>
                            <Button
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => void handleAttesaProvaConforme()}
                            >
                                Sì, conforme
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Shared Link Dialog */}
            {shareDialogAttachment && (
                <SharedLinkDialog
                    prescriptionId={prescriptionId}
                    attachment={shareDialogAttachment}
                    defaultPatientName={prescription?.NomePaziente}
                    defaultPrescriptionNumber={
                        prescription ? prescriptionNumberValue(prescription) : undefined
                    }
                    onClose={() => setShareDialogAttachment(null)}
                />
            )}

            {/* Feedback Modal */}
            {showFeedbackModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-xl font-bold">Feedback Prescrizione</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {Object.keys(feedbacks).length === 2 && Object.keys(feedbacks).includes('arcata_superiore') 
                                        ? 'Fornisci feedback per entrambe le arcate'
                                        : Object.keys(feedbacks).some(key => key.includes('arcata'))
                                        ? 'Fornisci feedback per l\'arcata selezionata'
                                        : 'Fornisci feedback per ogni dente selezionato'
                                    }
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowFeedbackModal(false)}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>

                        <div className="space-y-4">
                            {Object.entries(feedbacks).map(([tooth, feedback]) => (
                                <div key={tooth} className="border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-medium">{getFeedbackZoneLabel(tooth)}</h3>
                                        <div className="flex space-x-2">
                                            <Button
                                                size="sm"
                                                variant={feedback.type === 'positive' ? 'default' : 'outline'}
                                                onClick={() => handleFeedbackChange(tooth, 'positive', feedback.notes)}
                                                className={feedback.type === 'positive' ? 'bg-green-600 hover:bg-green-700' : ''}
                                            >
                                                <ThumbsUp className="w-4 h-4 mr-1" />
                                                Positivo
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant={feedback.type === 'negative' ? 'default' : 'outline'}
                                                onClick={() => handleFeedbackChange(tooth, 'negative', feedback.notes)}
                                                className={feedback.type === 'negative' ? 'bg-red-600 hover:bg-red-700' : ''}
                                            >
                                                <ThumbsDown className="w-4 h-4 mr-1" />
                                                Negativo
                                            </Button>
                                        </div>
                                    </div>
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                        rows={2}
                                        placeholder="Note aggiuntive (opzionale)..."
                                        value={feedback.notes}
                                        onChange={(e) => handleFeedbackChange(tooth, feedback.type, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => setShowFeedbackModal(false)}
                            >
                                Annulla
                            </Button>
                            <Button
                                onClick={handleSubmitFeedback}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                Invia Feedback
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* New Prova Modal */}
            {showNewProvaModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold">Nuova Prova</h2>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setShowNewProvaModal(false)
                                    setSelectedNewProva('')
                                    setNewProvaDate('')
                                    setNewProvaTime('')
                                    setNewProvaNotes('')
                                    setNewProvaScanner(undefined)
                                }}
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Seleziona fase di lavoro <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedNewProva}
                                    onChange={(e) => setSelectedNewProva(e.target.value)}
                                    disabled={availableTests === undefined || availableTests.length === 0}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="" disabled>
                                        {availableTests === undefined
                                            ? 'Caricamento fasi...'
                                            : availableTests.length === 0
                                                ? 'Nessuna fase configurata'
                                                : 'Seleziona una prova...'}
                                    </option>
                                    {availableTests?.map((test: any) => (
                                        <option
                                            key={test.id}
                                            value={test.id.toString()}
                                            disabled={test.selection_outcome === 'blocked'}
                                        >
                                            {laboratoryTestLabel(test)}
                                            {test.is_current ? ' — ripeti fase corrente' : ''}
                                            {test.is_immediate_next ? ' — fase successiva' : ''}
                                            {test.selection_outcome === 'blocked'
                                                ? ' — salto non consentito'
                                                : test.selection_outcome === 'approval_required'
                                                    ? ' — richiede approvazione'
                                                    : ''}
                                        </option>
                                    ))}
                                </select>
                                {availableTests?.length === 0 && (
                                    <p className="mt-2 text-sm text-red-600">
                                        Non risultano fasi configurate per questa società e prestazione.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Data Consegna
                                </label>
                                <Input
                                    type="date"
                                    value={newProvaDate}
                                    onChange={(e) => setNewProvaDate(e.target.value)}
                                    className="w-full"
                                    min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Ora Consegna
                                </label>
                                <Input
                                    type="time"
                                    value={newProvaTime}
                                    onChange={(e) => setNewProvaTime(e.target.value)}
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Note
                                </label>
                                <textarea
                                    value={newProvaNotes}
                                    onChange={(e) => setNewProvaNotes(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                                    placeholder="Inserisci eventuali note..."
                                />
                            </div>

                            <div>
                                <label htmlFor="newProvaScanner" className="block text-sm font-medium text-gray-700 mb-2">
                                    Modalità impronta
                                </label>
                                <select
                                    id="newProvaScanner"
                                    value={newProvaScanner === undefined ? 'inherit' : newProvaScanner ? 'digital' : 'physical'}
                                    onChange={(event) => {
                                        const value = event.target.value
                                        setNewProvaScanner(value === 'inherit' ? undefined : value === 'digital')
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="inherit">Mantieni la modalità della fase precedente</option>
                                    <option value="digital">Scansione intraorale</option>
                                    <option value="physical">Impronta fisica / presa corriere</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowNewProvaModal(false)
                                    setSelectedNewProva('')
                                    setNewProvaDate('')
                                    setNewProvaTime('')
                                    setNewProvaNotes('')
                                    setNewProvaScanner(undefined)
                                }}
                            >
                                Annulla
                            </Button>
                            <Button
                                onClick={handleCreateNewProva}
                                disabled={!selectedNewProva || availableTests === undefined || availableTests.length === 0}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                Crea Nuova Prova
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Modal */}
            {showNotificationModal && notificationConfig && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold flex items-center">
                                {notificationConfig.type === 'success' && (
                                    <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
                                )}
                                {notificationConfig.type === 'error' && (
                                    <AlertCircle className="w-6 h-6 text-red-600 mr-2" />
                                )}
                                {notificationConfig.type === 'confirm' && (
                                    <MessageSquare className="w-6 h-6 text-blue-600 mr-2" />
                                )}
                                {notificationConfig.title}
                            </h2>
                            {notificationConfig.type !== 'confirm' && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={closeNotificationModal}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            )}
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-700">{notificationConfig.message}</p>
                        </div>

                        <div className="flex justify-end space-x-3">
                            {notificationConfig.type === 'confirm' ? (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            if (notificationConfig.onCancel) {
                                                notificationConfig.onCancel()
                                            } else {
                                                closeNotificationModal()
                                            }
                                        }}
                                    >
                                        {notificationConfig.cancelText || 'Annulla'}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            notificationConfig.onConfirm?.()
                                        }}
                                        className="bg-primary hover:bg-primary/90"
                                    >
                                        {notificationConfig.confirmText || 'Conferma'}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={closeNotificationModal}
                                    className={
                                        notificationConfig.type === 'success'
                                            ? "bg-green-600 hover:bg-green-700"
                                            : "bg-red-600 hover:bg-red-700"
                                    }
                                >
                                    OK
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
